import { z } from 'zod'

export const asymmetricWeightSchema = z.object({
  positive: z.number(),
  negative: z.number(),
})
export type AsymmetricWeight = z.infer<typeof asymmetricWeightSchema>

// base shapes carry no .default(null) on limit/pivot. the full factor schema adds the
// defaults via .extend(); the override schema is the bare .partial() of the base — so a
// full FactorWeights always has limit/pivot present, while a sparse override stays sparse
// (omitted limit/pivot are absent, not injected as null, which would wipe the base pivot).
// limit = optional abs clamp on the normalized input (null = no clamp); pivot = optional
// avg-normalization pivot, norm = (input - pivot) / pivot (null = raw input).

// factors that respond differently to positive vs negative input
const asymmetricFactorBaseSchema = z.object({
  enabled: z.boolean(),
  dropWeight: asymmetricWeightSchema,
  shiftWeight: asymmetricWeightSchema,
  limit: z.number().positive().nullable(),
  pivot: z.number().positive().nullable(),
})
const asymmetricFactorSchema = asymmetricFactorBaseSchema.extend({
  limit: z.number().positive().nullable().default(null),
  pivot: z.number().positive().nullable().default(null),
})

// factors with a single scalar response
const scalarFactorBaseSchema = z.object({
  enabled: z.boolean(),
  dropWeight: z.number(),
  shiftWeight: z.number(),
  limit: z.number().positive().nullable(),
  pivot: z.number().positive().nullable(),
})
const scalarFactorSchema = scalarFactorBaseSchema.extend({
  limit: z.number().positive().nullable().default(null),
  pivot: z.number().positive().nullable().default(null),
})

export const factorWeightsSchema = z.object({
  disproportion: asymmetricFactorSchema,
  avgCostDistance: asymmetricFactorSchema,
  freeCash: asymmetricFactorSchema,
  accountCash: asymmetricFactorSchema,
  lotPrice: scalarFactorSchema,
  mood: scalarFactorSchema,
  desire: scalarFactorSchema,
})

export type FactorWeights = z.infer<typeof factorWeightsSchema>

// sparse override (per-instrument / per-backtest-instrument): the no-default base made
// fully optional. Zod 4 removed `.deepPartial()`, so .partial() each factor; a provided
// dropWeight/shiftWeight must still be a complete weight (merge replaces it wholesale).
const asymmetricFactorOverrideSchema = asymmetricFactorBaseSchema.partial()
const scalarFactorOverrideSchema = scalarFactorBaseSchema.partial()

export const factorWeightsOverrideSchema = z.object({
  disproportion: asymmetricFactorOverrideSchema.optional(),
  avgCostDistance: asymmetricFactorOverrideSchema.optional(),
  freeCash: asymmetricFactorOverrideSchema.optional(),
  accountCash: asymmetricFactorOverrideSchema.optional(),
  lotPrice: scalarFactorOverrideSchema.optional(),
  mood: scalarFactorOverrideSchema.optional(),
  desire: scalarFactorOverrideSchema.optional(),
})

export type FactorWeightsOverride = z.infer<typeof factorWeightsOverrideSchema>

// Approach-B defaults. disproportion ON (old-bot PERCENT_RATES / STEP_RATES); the
// other five OFF (enabled=false) so they are inert until opted in. The drop set
// mirrors the old PERCENT_RATES, the shift set the old STEP_RATES.
export const DEFAULT_FACTOR_WEIGHTS: FactorWeights = {
  disproportion: {
    enabled: true,
    dropWeight: { positive: 5, negative: 0.05 },
    shiftWeight: { positive: 5, negative: 0.3 },
    limit: null,
    pivot: null,
  },
  avgCostDistance: {
    enabled: false,
    dropWeight: { positive: 0.05, negative: 0.03 },
    shiftWeight: { positive: 0.05, negative: 0.03 },
    limit: 30,
    pivot: null,
  },
  freeCash: {
    enabled: false,
    dropWeight: { positive: -0.5, negative: -2 },
    shiftWeight: { positive: -0.5, negative: -1 },
    limit: null,
    pivot: 100000,
  },
  accountCash: {
    enabled: false,
    dropWeight: { positive: -0.5, negative: -2 },
    shiftWeight: { positive: -0.5, negative: -1 },
    limit: null,
    pivot: 100000,
  },
  lotPrice: { enabled: false, dropWeight: 0.2, shiftWeight: 0.3, limit: null, pivot: 1000 },
  mood: { enabled: false, dropWeight: -0.5, shiftWeight: -0.2, limit: null, pivot: null },
  desire: { enabled: false, dropWeight: -0.2, shiftWeight: -0.05, limit: null, pivot: null },
}
